import { useAxiosCreate } from '@/backend/utils';
import { downloadBlobResponse, filenameFromDisposition } from '@/backend/download';

const http = useAxiosCreate('api');

// Дата-основа периода/корзин:
// stat_date (событие карточки) | card_create_dt | closed_at | win_date (день аукциона)
// | load_date (плановая дата погрузки)
export const DATE_BASIS_OPTIONS = [
  { value: 'stat_date', label: 'Событие' },
  { value: 'card_create_dt', label: 'Создание карточки' },
  { value: 'closed_at', label: 'Закрытие' },
  { value: 'win_date', label: 'День аукциона' },
  { value: 'load_date', label: 'Дата погрузки' },
];

export function buildStatisticsParams(filters) {
  const params = {};
  if (!filters) return params;
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.dateBasis && filters.dateBasis !== 'stat_date') {
    params.date_basis = filters.dateBasis;
  }
  if (filters.logists?.length) params.logists = filters.logists.join(',');
  if (filters.linkedCities) {
    // Объединённый режим: город ищется и в отправке, и в назначении
    if (filters.citiesAny?.length) params.cities_any = filters.citiesAny.join(',');
  } else {
    if (filters.citiesOut?.length) params.cities_out = filters.citiesOut.join(',');
    if (filters.citiesIn?.length) params.cities_in = filters.citiesIn.join(',');
  }
  if (filters.segments?.length) params.segments = filters.segments.join(',');
  if (filters.clientCategories?.length) {
    params.client_categories = filters.clientCategories.join(',');
  }
  if (filters.statuses?.length) params.statuses = filters.statuses.join(',');
  if (filters.execution?.length) params.execution = filters.execution.join(',');
  if (filters.closingTiming?.length) params.closing_timing = filters.closingTiming.join(',');
  if (filters.bot === 'bot' || filters.bot === 'human') params.bot = filters.bot;
  if (filters.trip) params.trip = filters.trip;
  if (filters.onlyNew) params.only_new = true;
  return params;
}

export function fetchFilterOptions() {
  return http.get('statistics/filter-options').then(({ data }) => data);
}

export function fetchKpis(filters) {
  return http.get('statistics/kpis', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function fetchTrips({ filters, sortBy = 'commission_rub', asc = false, limit = 50, offset = 0 }) {
  return http
    .get('statistics/trips', {
      params: {
        ...buildStatisticsParams(filters),
        sort_by: sortBy,
        asc,
        limit,
        offset,
      },
    })
    .then(({ data }) => data);
}

export function fetchTripsByDay(filters) {
  return http.get('statistics/trips-by-day', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function fetchTemporal(filters, step = 'day') {
  return http
    .get('statistics/temporal', { params: { ...buildStatisticsParams(filters), step } })
    .then(({ data }) => data);
}

export function fetchTemporalByLogist(filters, step = 'day') {
  return http
    .get('statistics/temporal/by-logist', {
      params: { ...buildStatisticsParams(filters), step },
    })
    .then(({ data }) => data);
}

export function fetchTemporalByDirection(filters, step = 'day', minTrips = 1) {
  return http
    .get('statistics/temporal/by-direction', {
      params: { ...buildStatisticsParams(filters), step, min_trips: minTrips },
    })
    .then(({ data }) => data);
}

export function fetchByLogist(filters) {
  return http.get('statistics/by-logist', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function fetchByDirection(filters, minTrips = 5) {
  return http
    .get('statistics/by-direction', { params: { ...buildStatisticsParams(filters), min_trips: minTrips } })
    .then(({ data }) => data);
}

export function fetchDirectionLifecycle(filters, minTrips = 1) {
  return http
    .get('statistics/direction-lifecycle', {
      params: { ...buildStatisticsParams(filters), min_trips: minTrips },
    })
    .then(({ data }) => data);
}

export function fetchSegments(filters) {
  return http.get('statistics/segments', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function fetchNewTrips(filters) {
  return http.get('statistics/new-trips', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function fetchStatusSummary(filters) {
  return http.get('statistics/status-summary', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function fetchStatusByLogist(filters) {
  return http.get('statistics/status-by-logist', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function fetchBotSummary(filters) {
  return http.get('statistics/bot-summary', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function fetchBotByDay(filters) {
  return http.get('statistics/bot-by-day', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function fetchBotAuctions(filters) {
  return http.get('statistics/bot-auctions', { params: buildStatisticsParams(filters) }).then(({ data }) => data);
}

export function buildDowntimeParams(filters) {
  const params = {};
  if (!filters) return params;
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.responsibles?.length) params.responsibles = filters.responsibles.join(',');
  if (filters.classifications?.length) params.classifications = filters.classifications.join(',');
  if (filters.lifecycles?.length) params.lifecycles = filters.lifecycles.join(',');
  if (filters.operationTypes?.length) params.operation_types = filters.operationTypes.join(',');
  return params;
}

export function fetchDowntimeKpis(filters) {
  return http.get('statistics/downtimes/kpis', { params: buildDowntimeParams(filters) }).then(({ data }) => data);
}

export function fetchDowntimeByLogist(filters) {
  return http.get('statistics/downtimes/by-logist', { params: buildDowntimeParams(filters) }).then(({ data }) => data);
}

export function fetchDowntimeByWarehouse(filters, limit = 20) {
  return http
    .get('statistics/downtimes/by-warehouse', { params: { ...buildDowntimeParams(filters), limit } })
    .then(({ data }) => data);
}

export function fetchDowntimeByReason(filters) {
  return http
    .get('statistics/downtimes/by-reason', { params: buildDowntimeParams(filters) })
    .then(({ data }) => data);
}

export function fetchDowntimeByDay(filters) {
  return http.get('statistics/downtimes/by-day', { params: buildDowntimeParams(filters) }).then(({ data }) => data);
}

export function fetchDowntimeTable({ filters, sortBy = 'planned_date', asc = false, limit = 50, offset = 0 }) {
  return http
    .get('statistics/downtimes/table', {
      params: {
        ...buildDowntimeParams(filters),
        sort_by: sortBy,
        asc,
        limit,
        offset,
      },
    })
    .then(({ data }) => data);
}

export function fetchPivotConfig() {
  return http.get('statistics/pivot/config').then(({ data }) => data);
}

export function fetchPivotDimensionValues(filters, dim, limit = 300) {
  return http
    .get('statistics/pivot/dimension-values', {
      params: { ...buildStatisticsParams(filters), dim, limit },
    })
    .then(({ data }) => data);
}

export function fetchPivot({
  filters,
  x,
  y = null,
  metrics,
  step = 'day',
  xTop = 20,
  yTop = 15,
  xValues = null,
  yValues = null,
}) {
  const params = {
    ...buildStatisticsParams(filters),
    x,
    metrics: metrics.join(','),
    step,
    x_top: xTop,
    y_top: yTop,
  };
  if (y) params.y = y;
  if (xValues?.length) params.x_values = xValues.join(',');
  if (yValues?.length) params.y_values = yValues.join(',');
  return http.get('statistics/pivot', { params }).then(({ data }) => data);
}

export function fetchPivotTrips({
  filters,
  x,
  y = null,
  step = 'day',
  xValues = null,
  xKnown = null,
  yValues = null,
  yKnown = null,
  timeBins = null,
  sortBy = 'commission_rub',
  asc = false,
  limit = 50,
  offset = 0,
}) {
  const params = {
    ...buildStatisticsParams(filters),
    x,
    step,
    sort_by: sortBy,
    asc,
    limit,
    offset,
  };
  if (y) params.y = y;
  if (xValues?.length) params.x_values = xValues.join(',');
  if (xKnown?.length) params.x_known = xKnown.join(',');
  if (yValues?.length) params.y_values = yValues.join(',');
  if (yKnown?.length) params.y_known = yKnown.join(',');
  if (timeBins?.length) params.time_bins = timeBins.join(',');
  return http.get('statistics/pivot/trips', { params }).then(({ data }) => data);
}

export async function downloadPivotXlsx({
  filters,
  x,
  y = null,
  metrics,
  step = 'day',
  xTop = 20,
  yTop = 15,
  xValues = null,
  yValues = null,
}) {
  const params = {
    ...buildStatisticsParams(filters),
    x,
    metrics: metrics.join(','),
    step,
    x_top: xTop,
    y_top: yTop,
  };
  if (y) params.y = y;
  if (xValues?.length) params.x_values = xValues.join(',');
  if (yValues?.length) params.y_values = yValues.join(',');
  const response = await http.get('statistics/export/pivot.xlsx', {
    params,
    responseType: 'blob',
  });
  await downloadBlobResponse(response, 'statistics_pivot.xlsx');
}

export async function downloadPivotTripsXlsx({
  filters,
  x,
  y = null,
  step = 'day',
  xValues = null,
  xKnown = null,
  yValues = null,
  yKnown = null,
  timeBins = null,
  sortBy = 'commission_rub',
  asc = false,
}) {
  const params = {
    ...buildStatisticsParams(filters),
    x,
    step,
    sort_by: sortBy,
    asc,
  };
  if (y) params.y = y;
  if (xValues?.length) params.x_values = xValues.join(',');
  if (xKnown?.length) params.x_known = xKnown.join(',');
  if (yValues?.length) params.y_values = yValues.join(',');
  if (yKnown?.length) params.y_known = yKnown.join(',');
  if (timeBins?.length) params.time_bins = timeBins.join(',');
  const response = await http.get('statistics/export/pivot-trips.xlsx', {
    params,
    responseType: 'blob',
  });
  await downloadBlobResponse(response, 'statistics_pivot_trips.xlsx');
}

export async function downloadTripsCsv(filters) {
  const response = await http.get('statistics/export/trips.csv', {
    params: buildStatisticsParams(filters),
    responseType: 'blob',
  });
  if (response.status >= 400 || !response.data) {
    throw new Error(`Ошибка выгрузки (HTTP ${response.status})`);
  }
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filenameFromDisposition(response.headers['content-disposition'], 'statistics_trips.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Отчёты в стиле ТМ (сводка/фонды/автолидер/срывы/менеджеры/сегменты)
export function fetchTmReport(filters, client = 'efes') {
  return http
    .get('statistics/reports/tm', {
      params: { ...buildStatisticsParams(filters), client },
    })
    .then(({ data }) => data);
}

export async function downloadTmReportCsv(filters, client = 'efes') {
  const response = await http.get('statistics/reports/tm', {
    params: { ...buildStatisticsParams(filters), client, format: 'csv' },
    responseType: 'blob',
  });
  await downloadBlobResponse(response, `tm_report_${client}.csv`);
}
